import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/hooks/useAuth";
import type { ImpressaoConfigResponse } from "@/types/impressao-config-response";
import inovaone_api from "@/services/inovaone_api";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { alert } from "@/contexts/AlertModalContext";
import type { PadraoEPC } from "@/types/padrao-epc";
import type { EPCBlock } from "@/components/MinhaContaComponent";
import type { ListaZplItem } from "@/types/lista-zpl-item";

export type ImpressaoConfigContextType = {
  getImpressaoConfig: () => Promise<void>;
  impressaoConfig: ImpressaoConfigResponse | null;
  todosOsHardwaresIds: string[];
  coletarPadraoEpc: () => PadraoEPC;
  coletarBlocosEPC: () => EPCBlock[];
  coletaZplPadrao: () => ListaZplItem | undefined;
  criarImpressaoConfigPadrao: (
    configuracao_em_uso?: boolean,
    descricao_config?: string,
  ) => Promise<void>;
  listaImpressaoConfig: ImpressaoConfigResponse[];
  definirConfiguracaoComoPadrao: (id: string) => Promise<void>;
  apagarImpressaoConfig: (id_conta: string, id: string) => Promise<void>;
  clonarImpressaoConfig: (
    id_conta: string,
    config: ImpressaoConfigResponse,
  ) => Promise<void>;
  editarImpressaoConfig: (
    id_conta: string,
    id: string,
    descricao_config: string,
  ) => Promise<void>;
  atualizarEtiquetasImpressas: (quantidade: number) => Promise<void>;
  verificarSeEstaProximoDeAcabarORolo: () => boolean;
  zerarEtiquetasImpressas: () => Promise<void>;
  listarTodosOsHardwaresIds: () => Promise<void>;
};

// type SSCCEmpresa = {
//   descricao: string;
//   valor: string;
//   partition: number;
//   filter: number;
// };

// const SSCCEmpresas: SSCCEmpresa[] = [
//   { descricao: "iNOVACODE", valor: "7898968767", partition: 2, filter: 6 },
//   { descricao: "ELF", valor: "78989964417", partition: 1, filter: 0 },
// ];

export const ImpressaoConfigContext = createContext<
  ImpressaoConfigContextType | undefined
>(undefined);

export function ImpressaoConfigProvider({ children }: { children: ReactNode }) {
  const { setIsLoading } = useGlobalLoader();
  const { usuario } = useAuth();
  const [impressaoConfig, setImpressaoConfig] =
    useState<ImpressaoConfigResponse | null>(null);
  const [listaImpressaoConfig, setListaImpressaoConfig] = useState<
    ImpressaoConfigResponse[]
  >([]);
  const [todosOsHardwaresIds, setTodosOsHardwaresIds] = useState<string[]>([]);
  // Espelho síncrono do impressaoConfig — impressões em sequência chamam
  // atualizarEtiquetasImpressas antes do re-render, e o closure sozinho
  // entregaria o contador defasado (o total pararia de acumular).
  const impressaoConfigRef = useRef<ImpressaoConfigResponse | null>(null);

  useEffect(() => {
    impressaoConfigRef.current = impressaoConfig;
  }, [impressaoConfig]);

  const verificarSeEstaProximoDeAcabarORolo = () => {
    if (impressaoConfig?.tamanho_rolo && impressaoConfig?.etiquetas_impressas) {
      if (impressaoConfig.tamanho_rolo > 0) {
        // se faltar 10% ou menos, avisa
        const etiquetasRestantes =
          impressaoConfig.tamanho_rolo - impressaoConfig.etiquetas_impressas;
        const porcentagemRestante =
          (etiquetasRestantes / impressaoConfig.tamanho_rolo) * 100;
        if (porcentagemRestante <= 10) {
          return true;
        }
      }
    }
    return false;
  };

  const zerarEtiquetasImpressas = async () => {
    setIsLoading(true);
    const configAtual = impressaoConfigRef.current;
    if (!configAtual) {
      setIsLoading(false);
      return;
    }
    try {
      const newImpressaoConfig = {
        ...configAtual,
        etiquetas_impressas: 0,
      };
      impressaoConfigRef.current = newImpressaoConfig;
      setImpressaoConfig(newImpressaoConfig);
      // Envia só o campo alterado: mandar o objeto inteiro sobrescreveria na
      // base opções mudadas em outra aba/computador com a cópia antiga desta.
      await inovaone_api.post(`/impressao_config`, [
        { _id: configAtual._id, etiquetas_impressas: 0 },
      ]);
    } catch (e) {
      //console.log(e)
      // toast.error("Erro ao zerar numero de etiquetas impressas.");
    } finally {
      setIsLoading(false);
    }
  };

  const atualizarEtiquetasImpressas = async (quantidade: number) => {
    setIsLoading(true);
    const configAtual = impressaoConfigRef.current;
    if (!configAtual) {
      setIsLoading(false);
      return;
    }
    try {
      const etiquetasImpressas =
        (configAtual.etiquetas_impressas ?? 0) + quantidade;
      const newImpressaoConfig = {
        ...configAtual,
        etiquetas_impressas: etiquetasImpressas,
      };
      impressaoConfigRef.current = newImpressaoConfig;
      setImpressaoConfig(newImpressaoConfig);
      // Só o contador — ver zerarEtiquetasImpressas.
      await inovaone_api.post(`/impressao_config`, [
        { _id: configAtual._id, etiquetas_impressas: etiquetasImpressas },
      ]);
    } catch (e) {
      //console.log(e)
      // toast.error("Erro ao atualizar numero de etiquetas impressas.");
    } finally {
      setIsLoading(false);
    }
  };

  const definirConfiguracaoComoPadrao = async (id: string) => {
    if (!usuario || !usuario.id_conta) return;
    setIsLoading(true);
    try {
      let newListaConfiguracoesTrue = [];
      for (const item of listaImpressaoConfig) {
        if (item._id === id) {
          newListaConfiguracoesTrue.push({
            ...item,
            configuracao_em_uso: true,
          });
        }
      }

      let newListaConfiguracoesFalse = [];
      for (const item of listaImpressaoConfig) {
        if (item.configuracao_em_uso === true) {
          newListaConfiguracoesFalse.push({
            ...item,
            configuracao_em_uso: false,
          });
        }
      }

      if (newListaConfiguracoesFalse.length > 0) {
        await inovaone_api.post(`/impressao_config`, newListaConfiguracoesTrue);
      }
      if (newListaConfiguracoesFalse.length > 0) {
        await inovaone_api.post(
          `/impressao_config`,
          newListaConfiguracoesFalse,
        );
      }
      getImpressaoConfig();
    } catch (error) {
      alert.error("Erro ao definir configuração como padrão.");
    } finally {
      setIsLoading(false);
    }
  };

  const coletaZplPadrao = (): ListaZplItem | undefined => {
    if (!impressaoConfig) return undefined;
    try {
      if (!impressaoConfig.etiqueta_padrao) {
        alert.error("Etiqueta padrão não definida.");
        return undefined;
      }
      if (impressaoConfig.lista_zpl.length === 0) {
        alert.error("Etiqueta padrão não definida.");
        return undefined;
      }

      if (
        !impressaoConfig.lista_zpl[parseInt(impressaoConfig.etiqueta_padrao)]
      ) {
        alert.error("Etiqueta padrão não definida.");
        return undefined;
      }

      const zplPadrao = impressaoConfig.lista_zpl.find(
        (_, index) =>
          index === (parseInt(impressaoConfig.etiqueta_padrao) ?? 0),
      );
      return zplPadrao ? { ...zplPadrao } : undefined;
    } catch (error) {
      alert.error("Erro ao coletar ZPL padrão, verifique a configuração.");
      return undefined;
    }
  };

  const coletarBlocosEPC = (): EPCBlock[] => {
    if (!impressaoConfig) return [];
    try {
      let blocks: EPCBlock[] = [];
      let epcPersonalizadoParsed = JSON.parse(
        impressaoConfig.epc_personalizado,
      );
      blocks = epcPersonalizadoParsed.map((block: EPCBlock) => ({
        id: block.id,
        nome: block.nome,
        removable: true,
      }));
      return blocks;
    } catch (error) {
      return [];
    }
  };

  const listarTodosOsHardwaresIds = async () => {
    try {
      const response = await inovaone_api.get<ImpressaoConfigResponse[]>(
        `/impressao_config/${usuario?.id_conta}/*`,
      );
      let todosHardwareIDs: string[] = response.data.map(
        (item: ImpressaoConfigResponse) => item.app_id,
      );
      todosHardwareIDs = [...new Set(todosHardwareIDs)];
      todosHardwareIDs = todosHardwareIDs.filter((item) => item !== "");
      setTodosOsHardwaresIds(todosHardwareIDs);
    } catch (error) {
      alert.error("Erro ao buscar IDs de hardwares, tente novamente.");
      throw new Error("Erro ao buscar IDs de hardwares, tente novamente.");
    }
  };

  const coletarPadraoEpc = () => {
    if (impressaoConfig?.padrao_epc) {
      try {
        return JSON.parse(impressaoConfig.padrao_epc).descricao.toLowerCase();
      } catch (error) {
        alert.error("Erro ao coletar padrão EPC, verifique a configuração.");
      }
    }
    return "";
  };

  useEffect(() => {
    if (impressaoConfig) {
      coletarPadraoEpc();
    }
  }, [impressaoConfig]);

  const getImpressaoConfig = async () => {
    if (!usuario || !usuario._id || !usuario.id_conta) {
      return;
    }
    try {
      const response_impressao_config = await inovaone_api.get<
        ImpressaoConfigResponse[]
      >(`/impressao_config/${usuario?.id_conta}/${usuario?._id}`);

      if (response_impressao_config.data.length === 0) {
        await criarImpressaoConfigPadrao();
        alert.success("Configuração de impressão padrão criada com sucesso.");
        return;
      }

      // ✅ Normaliza a lista adicionando descricao_config quando não existir
      const listaNormalizada = response_impressao_config.data.map(
        (config, index) => ({
          ...config,
          descricao_config:
            config.descricao_config ||
            `${usuario?.nome || "Perfil"} ${index + 1}`, // fallback amigável
        }),
      );

      setListaImpressaoConfig(listaNormalizada);

      const configEmUso = listaNormalizada.find(
        (config) => config.configuracao_em_uso,
      );

      if (configEmUso) {
        const configNormalizada = normalizeImpressaoConfig(
          configEmUso,
          usuario?.id_conta,
          usuario?._id,
        );
        setImpressaoConfig(configNormalizada);
      } else {
        const configNormalizada = normalizeImpressaoConfig(
          listaNormalizada[0],
          usuario?.id_conta,
          usuario?._id,
        );
        setImpressaoConfig(configNormalizada);
      }
    } catch (error) {
      alert.error("Erro ao buscar configurações da conta, tente novamente.");
      throw new Error(
        `Erro ao buscar configurações da conta, tente novamente.`,
      );
    }
  };

  const criarImpressaoConfigPadrao = async (
    configuracao_em_uso: boolean = true,
    descricao_config?: string,
  ) => {
    if (!usuario) {
      alert.error("Usuário não encontrado.");
      return;
    }
    try {
      const newImpressaoConfig: ImpressaoConfigResponse = {
        ...getDefaultImpressaoConfig(
          usuario.id_conta,
          usuario._id,
          configuracao_em_uso,
        ),
        descricao_config: descricao_config || "Novo Perfil",
      };

      await inovaone_api.post<ImpressaoConfigResponse[]>(`/impressao_config`, [
        newImpressaoConfig,
      ]);

      getImpressaoConfig();
    } catch (error) {
      alert.error("Erro ao criar configuração padrão, tente novamente.");
    }
  };

  const apagarImpressaoConfig = async (
    id_conta: string | undefined,
    _id: string | undefined,
  ) => {
    if (!id_conta || !_id) {
      return;
    }
    try {
      await inovaone_api.delete(`/impressao_config/${id_conta}?_id=${_id}`);
      getImpressaoConfig();
    } catch (error) {}
  };

  const clonarImpressaoConfig = async (
    id_conta: string,
    config: ImpressaoConfigResponse,
  ) => {
    if (!usuario?.id_conta) return;
    try {
      const { _id, configuracao_em_uso, ...resto } = config;

      const { data } = await inovaone_api.post<ImpressaoConfigResponse[]>(
        `/impressao_config`,
        [
          {
            ...resto,
            id_conta,
            configuracao_em_uso: false,
            descricao_config: config.descricao_config || "Perfil clonado",
          },
        ],
      );

      const novaConfig = data?.[0];
      if (!novaConfig || !novaConfig._id) {
        alert.error("Retorno da API inválido ao clonar configuração.");
        return;
      }

      setListaImpressaoConfig((prev) => {
        const index = prev.findIndex((p) => p._id === config._id);
        if (index === -1) return [...prev, novaConfig];

        const novaLista = [...prev];
        novaLista.splice(index + 1, 0, novaConfig);
        return novaLista;
      });

      alert.success("Configuração clonada com sucesso!");
    } catch (error) {
      alert.error("Erro ao clonar configuração.");
    }
  };

  const editarImpressaoConfig = async (
    id_conta: string,
    id: string,
    descricao_config: string,
  ): Promise<void> => {
    if (!usuario?.id_conta || !usuario?._id) {
      throw new Error("Usuário não encontrado");
    }

    // Pegue a config atual para enviar o OBJETO COMPLETO no POST
    const atual = listaImpressaoConfig.find((c) => c._id === id);
    if (!atual) {
      throw new Error("Configuração não encontrada na lista local.");
    }

    try {
      // Monte o payload completo, apenas alterando o nome
      const payload: ImpressaoConfigResponse = {
        ...atual,
        _id: id, // mesmo _id para ATUALIZAR
        id_conta, // garanta consistência
        id_usuario: (atual as any).id_usuario ?? usuario._id,
        descricao_config, // novo nome
        // mantenha TODOS os demais campos das abas como estão em "atual"
      };

      // A API espera ARRAY e retorna ARRAY
      const resp = await inovaone_api.post<ImpressaoConfigResponse[]>(
        `/impressao_config`,
        [payload],
      );

      const atualizado = Array.isArray(resp.data)
        ? resp.data[0]
        : (resp.data as any);
      const descFinal = atualizado?.descricao_config ?? descricao_config;

      // Atualize a lista local (sem getImpressaoConfig, evitando loops/websocket)
      setListaImpressaoConfig((prev) =>
        prev.map((cfg) =>
          cfg._id === id
            ? { ...cfg, ...atualizado, descricao_config: descFinal }
            : cfg,
        ),
      );

      // Se o perfil editado é o em uso, alinhe também
      setImpressaoConfig((prev) =>
        prev && prev._id === id
          ? { ...prev, ...atualizado, descricao_config: descFinal }
          : prev,
      );
    } catch (error) {
      alert.error("Erro ao editar configuração.");
    }
  };

  const getDefaultImpressaoConfig = (
    id_conta: string,
    id_usuario: string,
    configuracao_em_uso: boolean = true,
  ): ImpressaoConfigResponse => ({
    id_conta,
    id_usuario,
    endereco_api: "",
    lista_zpl: [
      {
        nome: "Etiqueta Sample",
        altura: "153",
        largura: "101",
        zpl: "^XA\n\n^MD7\n^LH5,10\n ^CP\n^KV\n^JU\n^MM\n^XB\n^MU\n^FB\n^TB\n\n^FO190,0^A0N,40,40^FD{serial}^FS\n^FO310,100^FR^GB220,120,60,B,2^FS\n\n^FX Top section with logo, name and address.\n^CF0,60\n^FO50,50^GB100,100,100^FS\n^FO75,75^FR^GB100,100,100^FS\n^FO93,93^GB40,40,40^FS\n^FO220,50^FDiNOVACODE AMOSTRA ZPL^FS\n^CF0,30\n^FO50,250^GB700,3,3^FS\n\n^FX Second section with recipient address and permit information.\n^CFA,30\n^FO50,300^FDJohn Doe^FS\n^CFA,15\n^FO600,300^GB150,150,3^FS\n^FO638,390^FD123456^FS\n^FO50,500^GB700,3,3^FS\n\n^FX Third section with bar code.\n^BY5,2,70\n^FO100,750^BC^FD12345678^FS\n^FO50,600^BY2,3,20^BCN,50,N^FD{serial}^FS\n^FO400,600^BY2,3.0^BEN,40,Y,N^FD{ean}^FS\n\n^FX Fourth section (the two boxes on the bottom).\n^FO50,900^GB700,250,3^FS\n^FO400,900^GB3,250,3^FS\n^CF0,40\n^FO100,960^FDCtr. X34B-1^FS\n\n^CF0,190\n^FO470,955^FDCA^FS\n\n^XZ\n\n~DGR:logo_cba.GRF,360,12,,783FFO0C,7CIFCM01F,7DJFM01F8,78JF8L01F8,70JFCL01F8,60JFEI01F81F8FI0FE,40JFEI07FE1FBFC07FF8,40F03FFI0IF1IFE07FFC,00C00FF001IF1JF07FFE,0080078001IF1JF07FFE,008003I03F811FC3F8407E,K02I03F001F81F8003F,K02I03E001F81F8063F,O07E001F81F87IF,O07E001F81F8JF,O03E001F81F8JF,01M03F001F81F9F83F,01I04I03F811F83F1F03F,078004I03IF1FDFF1F87F,2FC00CI01IF1IFE1JF,3FE03C0801IF1IFE0JF,1JFC0800IF1IFC0FFDF,1JFC18003FE07FF003F0F,0JFC38,07IFC38,03IFEF8,00IFCF8,003FF078,,\n\n~DGR:logo_cba.GRF,360,12,,783FFO0C,7CIFCM01F,7DJFM01F8,78JF8L01F8,70JFCL01F8,60JFEI01F81F8FI0FE,40JFEI07FE1FBFC07FF8,40F03FFI0IF1IFE07FFC,00C00FF001IF1JF07FFE,0080078001IF1JF07FFE,008003I03F811FC3F8407E,K02I03F001F81F8003F,K02I03E001F81F8063F,O07E001F81F87IF,O07E001F81F8JF,O03E001F81F8JF,01M03F001F81F9F83F,01I04I03F811F83F1F03F,078004I03IF1FDFF1F87F,2FC00CI01IF1IFE1JF,3FE03C0801IF1IFE0JF,1JFC0800IF1IFC0FFDF,1JFC18003FE07FF003F0F,0JFC38,07IFC38,03IFEF8,00IFCF8,003FF078,,\n\n~DGR:logo_TAG.GRF,2170,35,,:i03MFE,hP03CI07CI07NF,hP07EI0FEI0OF,003LFCI01EI01EgP0FFI0FFI0OF,07NFI03FI03FgP0FF001FF001E7MF,07NF8007F8007F8gN01E7001C7001E7MF,07NF8007F8007F8gN01C7001C7001E7MF,07MF380073800F38gN01C7001C7001C7MF,07MF180073C00F3CgN01C7001C7001C7MF,07MF1C0073C00F3CgN01C7001C7801C7MF,07LFE1C00F3C00F3CgN01C7801C7801C3MF,07LFE1C00E3C00E1CgN01C7801C3801C3MF,07LFE1C00E3C00E1CgN01C3801C3801C3MF,07LFE1C00E1C00E1CgN03C3803C3803C3MF,07LFE1C00E1C00E1CgN038380383803C3MF,:07LFE1C00E1E00E1CgN038380383803C3MF,:07LFE0E00E1E00E1CgN03838038380383MF,07LFE0E00E1E01E1EgH0FCJ038380383C0381MF,07LFE0E01E1E01E1EJ03YFCI0383C0383C0381MF,07LFE0E01E1E01E0EI01gFEI0383C0381C0381MF,07LFC0E01C0E01E0EI03gFEI0381C0381C0381MF,07LFC0E01C0E01C0EI03OF80OFEI0381C0381C0381MF,07LFC0E01C0E01C0EI07FX01EI0381C0381C0381MF,07LFC0E01C0E01C0EI078Y0EI0781C0781C0381MF,07LFC0E01C0F01C0EI078Y0EI0781C0701C0781MF,07LFC0E01C0F01C0EI078Y0FI0701C0701C0781MF,07LFC0F01C0F01C0FI078Y0EI0701C0701C0781MF,07LFC0F01C0F01C0FI078Y0FI0701E0701E0780MF,07LFC0701C0F01C0FI078Y0FI0701E0701E0780MF,07LFC0701C0701C07I078Y0FI0700E0701E0700MF,07LFC0703C0701C07I078Y0FI0700E0700E0700MF,07LFC070380703807I078Y0FI0700E0700E0700MF,:07LF8070380703807I078Y0FI0700E0700E0700MF,07LF8070380783807I078Y0FI0700E0700E0700MF,07LF807838078380780038Y0FI0F00E0700E0F00MF,07LF807838078380780038Y0FI0F00E0E00F0F00MF,07LF803838078380780038Y0FI0E00F0E00F0F007LF,07LF803838078380380038Y0FI0E00F0E00F0F007LF,07LF803838038380380038Y0FI0E0070E0070F007LF,07LF803838038380380038Y0FI0E0070E0070E007LF,07LF803878038380380038Y0FI0E0070E0070E007LF,07LF803878038380380038R0NFI0E0070E0070E007LF,07LF80387803878038003gFEI0E0070E0070E007LF,07LF80387003C70038003gFCI0E0070E0070E007LF,07LF003C7003C70038001gF8I0E0070E0078E007LF,07LF003C7003C70018I07QF8Q0E0070E0078E007LF,07LF001C7003C7001CQ07IFR0E0079E0079E003LF,07LF001C7001C7001CQ03FFEQ01C0039E0039E003LF,07LF001C7001C7001CQ03FFEQ01C0039C0039E003LF807LF001C7001C7001CQ03IFQ01C003FC003FE003LF807LF001CF001CF001CQ0JF807OFC003FC003FC003LF807LF001EF001FF001E00gKFC001FC001F8001LF,07LFI0FE001FEI0gNF8001FJ0F8I03JFE,03KFEI0FEI0FEI0gHF,03KFCI07CI07CI03FF,00JFE,,\n\n~DGR:logo_inova.GRF,2160,36,,::gI02,:gI06,gI07,:gI0F,gI0F8,gH01F8,gH03F8,gH03FC,Y06007FC,Y0781FFE,Y03KF,Y03KF8,Y03KFC,Y01LF018,Y01NF,g0NFJ0FC,g0NFI07FC,g07MF001FFC,g07LFE007FFC,1F01FJ03C01LF83LFE00IF8001KF800LF8003JFEJ07KF8,1F01FCI03E03LFC3LFE01FEFI03KFC01LFE003KF8I0LF8,1F01FEI03E07EJ07C1LFC07F0FI07CI03E03FJ07E003KFEI0LF,1F01FF8003E07CJ07C0LFC07C0FI078I03E03EJ03E003CI07FI0FC,1F01FFC003E07CJ07C0LF80F80FI078I03E03EJ03E003CI01F800FC,1F01F7F003E07CJ07C07KF81E00FI078L03EJ03E003CJ0F800FC,1F01F1FC03E07CJ07C03KF03C00FI078L03EJ03E003CJ0FC00FC,1F01F0FE03E07CJ07C03KF03800FI078L03EJ03E003CJ07C00JF8,1F01F03F83E07CJ07C01JFE07I0FI078L03EJ03E003CJ07C00JF8,1F01F00FC3E07CJ07C00JFC0FI0FI078L03EJ03E003CJ0FC00FC,1F01F007F3E07CJ07C007IF80EI0FI078I01C03EJ03E003CJ0FC00FC,1F01F001FFE07CJ07C003IF80CI0FI078I03E03EJ03E003CI01F800FC,1F01FI0FFE07CJ03C001IF01CI0FI078I03E03EJ03E003CI03FI0FC,1F01FI03FE07EJ07CI0FFE01CI0FI07CI03E03FJ03E003KFEI0LF8,1F01FJ0FE03LFCI07FC0183IF8007KFC03LFE003KFCI0LF8,1F01EJ07C01LF8I01F80381IFI03KF801LFC003KFJ07KF8,gJ0E0038,gJ04003,gM03,:gM07,gM07U06001T02M0C,gM0601FC0FE10FF8003F81FE02008067FE303FC0E0107F,gM0601860C010C0E006003018200806020306060F010C,gM0601030C010C0200600600820080602030C030D810C,gM0601830C010C0300600400C200806020308018C810C,gM0201030C010C0100300C004200806020318018CC106,gM0201860C010C01001C0C004200806020318008C61038,gM0201F80FE10C01I070C004200806020318008C3100E,gM02018C0C010C01I018C004200806020318018C11003,gM0201060C010C03J0C400C200806020318018C190018,gO01820C010C06J0C6008200C0402030C010C0D0018,gO01830C010C0CJ0C3018200C0C020306060C070018,gO01818C010FF8007F81FE03F87F8020303FC0C030FF,hH01C0038K0EM0FL038,,\n\n\n^XA\n^CI28\n^CF0,30,30\n^MD15\n^FS\n^FO50,250^GB700,3,3^FS\n^FO50,750^GB700,3,3^FS\n^FO50,550^GB700,3,3^FS\n^FO60,60^FDAO CONSUMIR DEPOSITAR^FS\n^FO460,730^A0N,20,20^FDDOBRAR^FS\n^FO200,760^A0N,20,20^FDCOLAR ESTE LADO PRIMEIRO^FS\n^FO70,90^FDNA CAIXA DE BAIXA^FS\n^FO200,400^BQN,2,4,Q,7^FDMANhttp://www.inovacode.com.br^FS\n^FO20,1000^FDCódigo do material:^FS\n^FO440,1000^FDDescrição breve do material:^FS\n^FO440,1040^FD{desc}^FS\n^FO20,1040^A0N,30,30^FD{info1}^FS\n^FO20,1100^FD*Fornecedor:^FS\n^FO20,1140^FD{info2}^FS\n^FO550,1100^FDData:^FS\n^FO550,1140^FD{info3}^FS\n^FO20,1180^FD*Numero de controle:^FS\n^FO20,1210^FD{info3}^FS\n^FO550,1180^FD*Quantidade:^FS\n^FO550,1210^FD{info4}^FS\n^FO400,940^A0N,20,20^FDRFiD:{epc}^FS\n^FO450,60^BY3^BCN,50,Y,N,N^FD{ean}^FS\n^FO20,1218^A0N,20,20^FD*campos não obrigatórios^FS\n^FO50,930^XGR:logo_cba.GRF,1,1^FS\n^FO120,580^XGR:logo_TAG.GRF,2,2^FS\n^FO400,430^XGR:logo_inova.GRF,1,1^FS\n^XZ\n\n^XA\n^RFW,H^FD{epc}^FS\n^FO260,25^FR^GB300,30,80^FS\n^FS^FO560,30^FB600,2,5^A0N,45,45^FD{desc}\n^FS^FO80,300^A0N,45,45^FD{ean}\n^FS^FO530,315^A0N,45,45^FDRFiD:{epc}\n^FS^FO1099,113^A0N,50,50^FWB,0^FD{info3}\n^FS^XZ",
        dpmm: "203",
      },
    ],
    montagem_epc: "",
    posicao_gtin_impressao: "1",
    campo_aberto_epc: "",
    api_externa: "",
    api_externa_erro: "",
    token_checagem: "",
    opcao_base: "one",
    potencia_padrao: "2",
    etiqueta_padrao: "0",
    serializar_epc: "true",
    token_impressao: "",
    app_id: "",
    impressao_log_erro: "",
    campo_descricao_ordem_impressao: "0",
    opcoes_info_compl: "",
    localizacao_padrao: "Estoque",
    tempo_delay_gravacao: "720",
    epc_personalizado:
      '[{"nome":"Ano (AA)","id":"{aa}"},{"nome":"Ano (AAAA)","id":"{aaaa}"},{"nome":"Mês (MM)","id":"{mm}"},{"nome":"D","id":"{D}"},{"nome":"Código","id":"{codigo}"},{"nome":"C","id":"{C}"},{"nome":"<<>>","id":"{<<>>}"},{"nome":"Serial","id":"{serial}"}]',
    epc_filter: "",
    impressao_usb: "false",
    lista_logo: [],
    converter_codigo_ordem: false,
    conta_em_manutencao: false,
    impressao_legacy: "false",
    converter_gs1_ordem: false,
    padrao_epc: '{"descricao":"Personalizado"}',
    company_prefix: '{"descricao":"6 digitos","valor":"6"}',
    filter_epc: '{"descricao":"0 - all other","valor":"0"}',
    apenas_gravar: false,
    campo_descricao_xml: "xProd",
    campo_codigo_xml: "cProd",
    campo_info1_xml: "",
    campo_info2_xml: "",
    campo_info3_xml: "",
    campo_info4_xml: "",
    campo_info5_xml: "",
    campo_info6_xml: "",
    campo_info7_xml: "",
    campo_info8_xml: "",
    campo_info9_xml: "",
    campo_info10_xml: "",
    campo_info11_xml: "",
    campo_info12_xml: "",
    campo_valor_xml: "",
    campo_valor_unidade_xml: "",
    campo_qtde_xml: "",
    campo_qtde_tags_xml: "",
    campo_validade_xml: "",
    localizacao_padrao_2: "",
    localizacao_padrao_3: "",
    localizacao_padrao_4: "",
    epc_personalizado_planilha: true,
    epc_personalizado_of_ref: true,
    comando_zpl_inicial: "",
    comando_zpl_final: "",
    comando_zpl_intermediario: "",
    impressao_por_item: false,
    configuracao_em_uso,
    travar_etiqueta: true,
    tentativas_gravacao: "1",
    filtro_ordem: "",
    rede_secundaria: "",
    senha_secundaria: "",
    campo_ordem_loc1: "",
    campo_ordem_loc2: "",
    campo_ordem_loc3: "",
    campo_ordem_loc4: "",
    campo_ordem_tag: "",
    inicio_tag_virgem: "E280",
    tag_virgem_maior_epc: true,
    interface_simples: false,
    senha_config: "",
    tratativa_automatica_ordem: "MARCAR DEFEITO E REIMPRIMR",
    tratativa_automatica_tentativas_ordem: 0,
    zpl_marcacao_defeito:
      "^XA  ^LH120,20  ^FO0,20 ^FO30,20^A0N,30,30^FDETIQUETA ANTERIOR COM DEFEITO^FS  ^FO180,50^A0N,25,25^FD@inovacode^FS ^XZ",
    conferir_tag_apos_gravar: true,
    tratativa_detectar_ja_gravada: true,
    tratativa_gravada_pela_metade: true,
    tratativa_epc_corrompido_miolo: true,
    tratativa_falso_positivo_erro: true,
    tratativa_proxima_tag_virgem: true,
    tratativa_blacklist: true,
    tratativa_aumento_potencia: true,
    salvar_itens_no_final: false,
    gravacao_reversa: false,
    webhook_ordem_finalizada: "",
    modo_teste: false,
    modo_teste_atualizar_serial: false,
    descricao_config: "Padrão",
    hardwareid_alternativo: "",
    etiquetas_impressas: 0,
    tamanho_rolo: 5000,
    campo_id_externo: "",
    travar_quantidade_erp: false,
    campo_quantidade_pedidos: "",
    campo_info_compl_10_ordem_impressao: "",
    campo_info_compl_1_ordem_impressao: "",
    campo_info_compl_2_ordem_impressao: "",
    campo_info_compl_3_ordem_impressao: "",
    campo_info_compl_4_ordem_impressao: "",
    campo_info_compl_5_ordem_impressao: "",
    campo_info_compl_6_ordem_impressao: "",
    campo_info_compl_7_ordem_impressao: "",
    campo_info_compl_8_ordem_impressao: "",
    campo_info_compl_9_ordem_impressao: "",
    campo_info_compl_11_ordem_impressao: "",
    campo_info_compl_12_ordem_impressao: "",
    nivel_loc_1_pedidos: "",
    nivel_loc_2_pedidos: "",
    nivel_loc_3_pedidos: "",
    nivel_loc_4_pedidos: "",
  });

  const normalizeImpressaoConfig = (
    config: Partial<ImpressaoConfigResponse>,
    id_conta: string,
    id_usuario: string,
  ): ImpressaoConfigResponse => {
    return {
      ...getDefaultImpressaoConfig(id_conta, id_usuario),
      ...config,
    };
  };

  return (
    <ImpressaoConfigContext.Provider
      value={{
        getImpressaoConfig,
        impressaoConfig,
        todosOsHardwaresIds,
        coletarPadraoEpc,
        coletarBlocosEPC,
        coletaZplPadrao,
        criarImpressaoConfigPadrao,
        listaImpressaoConfig,
        definirConfiguracaoComoPadrao,
        apagarImpressaoConfig,
        clonarImpressaoConfig,
        editarImpressaoConfig,
        atualizarEtiquetasImpressas,
        verificarSeEstaProximoDeAcabarORolo,
        zerarEtiquetasImpressas,
        listarTodosOsHardwaresIds,
      }}
    >
      {children}
    </ImpressaoConfigContext.Provider>
  );
}

export function useImpressaoConfig() {
  const context = useContext(ImpressaoConfigContext);
  if (!context) {
    throw new Error(
      "useImpressaoConfig deve ser usado dentro de um ImpressaoConfigProvider",
    );
  }
  return context;
}
